import type { Scenario } from '../types'
import { buildMoreZhScenarios } from './scenariosMoreZh'
import { buildNumberScenariosZh } from './scenariosNumbersZh'

/**
 * Scenarios are what separate this from a flashcard deck. Each beat is a line
 * you did not choose, and your job is to recognise which island it calls for
 * and produce it before the silence gets long. Selecting the right island is
 * scored separately from delivering it, because in practice those fail apart:
 * you can know the sentence cold and still freeze on which one is wanted.
 */

/** Lowest possible sync clock — see SEED_CLOCK in seedIslands.ts. */
const SEED_CLOCK = 0

const freshStats = () => ({ runs: 0, bestScore: 0, lastScore: null, lastRunAt: null })

export function buildSeedScenarios(): Scenario[] {
  return [
    {
      id: 'scn_meeting',
      title: 'Meeting someone at dinner',
      titleZh: '认识新朋友',
      setting: '北京，朋友家的晚饭',
      brief:
        "A friend's dinner in Beijing. The man beside you turns and starts talking. Four of your islands cover everything he is about to ask — the only question is whether you can reach the right one in time.",
      language: 'zh',
      isStarter: true,
      updatedAt: SEED_CLOCK,
      stats: freshStats(),
      beats: [
        {
          id: 'scn_meeting_b1',
          speaker: '李伟',
          promptHanzi: '你好，我是李伟。请问你做什么工作？',
          promptPinyin: 'nǐ hǎo, wǒ shì Lǐ Wěi. qǐng wèn nǐ zuò shén me gōng zuò?',
          promptEnglish: "Hello, I'm Li Wei. What do you do for work?",
          cue: 'Say what you do and who you do it for.',
          expectIslandId: 'isl_work',
          expectBlockIds: ['isl_work_b1', 'isl_work_b2'],
          hint: 'Lead with the job title, then the company.',
        },
        {
          id: 'scn_meeting_b2',
          speaker: '李伟',
          promptHanzi: '听起来很有意思。你的中文说得不错，学了多久了？',
          promptPinyin: 'tīng qǐ lai hěn yǒu yì si. nǐ de zhōng wén shuō de bú cuò, xué le duō jiǔ le?',
          promptEnglish: "Sounds interesting. Your Chinese is good — how long have you studied?",
          cue: 'Give the duration, then why you started.',
          expectIslandId: 'isl_why_chinese',
          expectBlockIds: ['isl_why_chinese_b1', 'isl_why_chinese_b2'],
          hint: 'The compliment is the trigger. This island should be automatic.',
        },
        {
          id: 'scn_meeting_b3',
          speaker: '李伟',
          promptHanzi: '你是一个人来的吗？家里人呢？',
          promptPinyin: 'nǐ shì yí ge rén lái de ma? jiā lǐ rén ne?',
          promptEnglish: 'Did you come alone? What about your family?',
          cue: 'Account for your household.',
          expectIslandId: 'isl_family',
          expectBlockIds: ['isl_family_b1', 'isl_family_b2'],
        },
        {
          id: 'scn_meeting_b4',
          speaker: '李伟',
          promptHanzi: '那你不上班的时候喜欢做什么？',
          promptPinyin: 'nà nǐ bú shàng bān de shí hou xǐ huan zuò shén me?',
          promptEnglish: "So what do you like doing when you're not working?",
          cue: 'Name two things, then go one level deeper on one of them.',
          expectIslandId: 'isl_hobbies',
          expectBlockIds: ['isl_hobbies_b1', 'isl_hobbies_b2'],
        },
      ],
    },
    {
      id: 'scn_restaurant',
      title: 'Dinner for two',
      titleZh: '在餐厅',
      setting: '一家普通的中餐厅，晚上七点',
      brief:
        'A busy restaurant at seven. The server is moving fast and will not slow down for you. Every beat here is a sentence you already have — this is a speed test, not a comprehension one.',
      language: 'zh',
      isStarter: true,
      updatedAt: SEED_CLOCK,
      stats: freshStats(),
      beats: [
        {
          id: 'scn_restaurant_b1',
          speaker: '服务员',
          promptHanzi: '几位？',
          promptPinyin: 'jǐ wèi?',
          promptEnglish: 'How many?',
          cue: 'Answer in one breath.',
          expectIslandId: 'isl_restaurant',
          expectBlockIds: ['isl_restaurant_b1'],
        },
        {
          id: 'scn_restaurant_b2',
          speaker: '服务员',
          promptHanzi: '这边请。看好了叫我。',
          promptPinyin: 'zhè biān qǐng. kàn hǎo le jiào wǒ.',
          promptEnglish: 'This way. Call me when you have decided.',
          cue: 'Stop them before they leave — you want an English menu.',
          expectIslandId: 'isl_restaurant',
          expectBlockIds: ['isl_restaurant_b2'],
          hint: '请问 buys you the half-second you need.',
        },
        {
          id: 'scn_restaurant_b3',
          speaker: '服务员',
          promptHanzi: '您要点什么？',
          promptPinyin: 'nín yào diǎn shén me?',
          promptEnglish: 'What would you like to order?',
          cue: 'Order two dishes.',
          expectIslandId: 'isl_restaurant',
          expectBlockIds: ['isl_restaurant_b3'],
        },
        {
          id: 'scn_restaurant_b4',
          speaker: '服务员',
          promptHanzi: '要辣的吗？',
          promptPinyin: 'yào là de ma?',
          promptEnglish: 'Do you want it spicy?',
          cue: 'Turn the heat down without refusing outright.',
          expectIslandId: 'isl_restaurant',
          expectBlockIds: ['isl_restaurant_b4'],
        },
        {
          id: 'scn_restaurant_b5',
          speaker: '服务员',
          promptHanzi: '还需要别的吗？',
          promptPinyin: 'hái xū yào bié de ma?',
          promptEnglish: 'Anything else?',
          cue: 'Close it out — ask for the bill.',
          expectIslandId: 'isl_restaurant',
          expectBlockIds: ['isl_restaurant_b5'],
        },
      ],
    },
    {
      id: 'scn_taxi',
      title: 'Taxi to the airport',
      titleZh: '打车去机场',
      setting: '早上六点，行李两件，航班十点',
      brief:
        'Six in the morning, two bags, a ten o\'clock flight. The driver asks short questions and expects short answers. Hesitation here costs you the expressway.',
      language: 'zh',
      isStarter: true,
      updatedAt: SEED_CLOCK,
      stats: freshStats(),
      beats: [
        {
          id: 'scn_taxi_b1',
          speaker: '师傅',
          promptHanzi: '去哪儿？',
          promptPinyin: 'qù nǎr?',
          promptEnglish: 'Where to?',
          cue: 'Destination, immediately.',
          expectIslandId: 'isl_taxi',
          expectBlockIds: ['isl_taxi_b1'],
        },
        {
          id: 'scn_taxi_b2',
          speaker: '师傅',
          promptHanzi: '走高速吗？高速要收费。',
          promptPinyin: 'zǒu gāo sù ma? gāo sù yào shōu fèi.',
          promptEnglish: 'Take the expressway? There is a toll.',
          cue: 'Yes — and say why, so it does not sound like you missed the toll part.',
          expectIslandId: 'isl_taxi',
          expectBlockIds: ['isl_taxi_b2'],
        },
        {
          id: 'scn_taxi_b3',
          speaker: '师傅',
          promptHanzi: '行，系好安全带。',
          promptPinyin: 'xíng, jì hǎo ān quán dài.',
          promptEnglish: 'Fine. Belt up.',
          cue: 'You need to know if you will make the flight.',
          expectIslandId: 'isl_taxi',
          expectBlockIds: ['isl_taxi_b3'],
        },
        {
          id: 'scn_taxi_b4',
          speaker: '师傅',
          promptHanzi: '快到了，您在哪儿下？',
          promptPinyin: 'kuài dào le, nín zài nǎr xià?',
          promptEnglish: 'Almost there — where do you want to get out?',
          cue: 'Pick a spot.',
          expectIslandId: 'isl_taxi',
          expectBlockIds: ['isl_taxi_b4'],
        },
        {
          id: 'scn_taxi_b5',
          speaker: '师傅',
          promptHanzi: '一共八十六块。',
          promptPinyin: 'yí gòng bā shí liù kuài.',
          promptEnglish: "That's eighty-six yuan altogether.",
          cue: 'You have no cash.',
          expectIslandId: 'isl_taxi',
          expectBlockIds: ['isl_taxi_b5'],
        },
      ],
    },
    ...buildMoreZhScenarios(),
    ...buildNumberScenariosZh(),
  ]
}
